import { RepeatIcon } from '../../../svg/repeat-icon';
import { LabelWithoutRef } from '../../label/label-without-ref';
import { TIME_ZONE_OPTIONS } from '../constants';
import { WorkflowScheduleValue } from '../types';
import { describeCronExpression, describeDateOrRelative } from '../utils/describe-schedule';
import { resolveDateOrRelative } from '../utils/relative-date';

interface ScheduleSummaryProps {
  value: WorkflowScheduleValue;
  relativeToTrigger?: boolean;
}

/** Plain-language preview of the current schedule, similar to the summary shown by calendar apps. */
export function ScheduleSummary({ value, relativeToTrigger = false }: ScheduleSummaryProps) {
  const timeZone = value.timeZone ?? 'America/Mexico_City';
  const timeZoneLabel =
    TIME_ZONE_OPTIONS.find((option) => option.value === timeZone)?.label ?? timeZone;

  if (value.type === 'scheduled') {
    return (
      <div className="text-xs">
        <div className="mb-4 space-y-1 border-l-2 border-accent pl-4">
          <p className="text-muted-foreground">Zona horaria {timeZoneLabel}</p>
        </div>

        <div>
          <LabelWithoutRef className="p-0 text-xs font-medium text-muted-foreground">
            Se ejecutará
          </LabelWithoutRef>
          <p className="text-sm">
            {describeDateOrRelative(value.executeAt, timeZone, 'start', relativeToTrigger)}
          </p>
        </div>
      </div>
    );
  }

  const startDate = resolveDateOrRelative(value.startsAt, timeZone) ?? new Date();
  const recurrenceDescription = describeCronExpression(value.recurrence.expression, startDate);

  return (
    <div className="text-xs">
      <div className="mb-4 space-y-1 border-l-2 border-accent pl-4">
        <p className="text-muted-foreground">Zona horaria {timeZoneLabel}</p>
        <div className="inline-flex items-center gap-2">
          <RepeatIcon className="shrink-0 text-success" />
          <p className="text-sm">
            <span className="uppercase">{recurrenceDescription.slice(0, 1)}</span>
            <span className="lowercase">{recurrenceDescription.slice(1)}</span>
          </p>
        </div>
      </div>

      <div className="mb-2">
        <LabelWithoutRef className="p-0 text-xs font-medium text-muted-foreground">
          Inicia
        </LabelWithoutRef>
        <p className="text-sm">
          {describeDateOrRelative(value.startsAt, timeZone, 'start', relativeToTrigger)}
        </p>
      </div>

      <div>
        <LabelWithoutRef className="p-0 text-xs font-medium text-muted-foreground">
          Termina
        </LabelWithoutRef>
        <p className="text-sm">
          {describeDateOrRelative(value.endsAt, timeZone, 'end', relativeToTrigger)}
        </p>
      </div>
    </div>
  );
}
